import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import nlIcon from '~/assets/landing/icons/nl.png';

const ORN_ICONS = ['i-snow', 'i-bows', 'i-run'];

function OrnLine({ reverse }: { reverse?: boolean }) {
  const icons = Array.from({ length: 48 }, (_, i) => ORN_ICONS[i % 3]);
  return (
    <div className="ornline">
      <div className="inner" style={reverse ? { animationDirection: 'reverse' } : undefined} aria-hidden="true">
        {icons.map((cls, i) => (
          <Fragment key={i}>
            <i className={`ico ${cls}`} />
            <i className="dia" />
          </Fragment>
        ))}
      </div>
    </div>
  );
}

type Audience = 'client' | 'partner';

/** «Как участвовать» (`section#how` макета): 4 шага и переключатель карта клиента / ID. */
export function HowTo() {
  const { t } = useTranslation('landing');
  const [audience, setAudience] = useState<Audience>('partner');
  const clientBtnRef = useRef<HTMLButtonElement>(null);
  const partnerBtnRef = useRef<HTMLButtonElement>(null);
  const [pill, setPill] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const btn = audience === 'client' ? clientBtnRef.current : partnerBtnRef.current;
    if (btn) setPill({ left: btn.offsetLeft, width: btn.offsetWidth });
  }, [audience]);

  useLayoutEffect(() => {
    const onResize = () => {
      const btn = audience === 'client' ? clientBtnRef.current : partnerBtnRef.current;
      if (btn) setPill({ left: btn.offsetLeft, width: btn.offsetWidth });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [audience]);

  const stepsData = t('how.steps', { returnObjects: true }) as Record<string, { title: string; text: string }>;
  const stepKeys: (keyof typeof stepsData)[] = ['step1', 'step2', 'step3', 'step4'];
  // Литеральные пути — каждый проходит строгую типизацию i18next по отдельности
  // (объект how.earn.cards целиком не достаём: там рядом ещё и строковый ключ registerCta).
  const purchaseCard = t('how.earn.cards.purchase', { returnObjects: true });
  const recruitCard = t('how.earn.cards.recruit', { returnObjects: true });
  const retentionCard = t('how.earn.cards.retention', { returnObjects: true });

  return (
    <section className="sec s-beige" id="how">
      <div className="wrap">
        <h2 className="h2 rv">{t('how.title')}</h2>
        <div className="steps rv">
          <OrnLine />
          <div className="stepwrap">
            {stepKeys.map((key, i) => (
              <div className="step" key={key} data-step="">
                <div className="num" data-n={String(i + 1).padStart(2, '0')}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div>
                  <h4>{stepsData[key].title}</h4>
                  <p>{stepsData[key].text}</p>
                </div>
              </div>
            ))}
          </div>
          <OrnLine reverse />
        </div>

        <div className="earn2 rv" id="earn" data-aud={audience}>
          <div className="e2head">
            <div>
              <h2>{t('how.earn.title')}</h2>
              <p>{t('how.earn.subtitle')}</p>
            </div>
            <div className="seg">
              <span>{t('how.earn.segLabel')}</span>
              <div className="segbtns">
                <i className="segpill" style={{ left: pill.left, width: pill.width }} />
                <button ref={clientBtnRef} type="button" className={audience === 'client' ? 'on' : ''} onClick={() => setAudience('client')}>
                  {t('how.earn.segClient')}
                </button>
                <button ref={partnerBtnRef} type="button" className={audience === 'partner' ? 'on' : ''} onClick={() => setAudience('partner')}>
                  {t('how.earn.segPartner')}
                </button>
              </div>
            </div>
          </div>
          <div className="e2cards">
            <EarnCard variant="all" illustration={<PurchaseIllustration />} card={purchaseCard} />
            <EarnCard variant="partner" illustration={<RecruitIllustration />} card={recruitCard} />
            <EarnCard variant="partner" illustration={<RetentionIllustration />} card={retentionCard} />
          </div>
          <p className="excl">{t('how.earn.exclusion')}</p>
        </div>
      </div>
    </section>
  );
}

function EarnCard({
  variant,
  illustration,
  card,
}: {
  variant: 'all' | 'partner';
  illustration: ReactNode;
  card: { bubbleValue: string; bubbleUnit: string; title: string; text: string; lockTitle: string; lockText: string };
}) {
  const { t } = useTranslation('landing');
  return (
    <article className={`e2card ${variant}`}>
      <div className="e2top">
        <div className="e2ill">{illustration}</div>
        <div className="bubble">
          <b>{card.bubbleValue}</b>
          <span>{card.bubbleUnit}</span>
        </div>
      </div>
      <h4>{card.title}</h4>
      <p>{card.text}</p>
      <div className="lockov">
        <span className="lk">🎁</span>
        <b className="lt">{card.lockTitle}</b>
        <small className="ls">{card.lockText}</small>
        <a className="lbtn" href="#" data-register="">
          {t('how.earn.cards.registerCta')} <span>→</span>
        </a>
      </div>
    </article>
  );
}

function PurchaseIllustration() {
  return (
    <svg viewBox="0 0 120 120">
      <defs>
        <filter id="bagLogo">
          <feFlood floodColor="#FBF4E6" />
          <feComposite in2="SourceAlpha" operator="in" />
        </filter>
        <linearGradient id="bgw" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#B42A40" />
          <stop offset="1" stopColor="#6E1222" />
        </linearGradient>
        <linearGradient id="btl" x1="0" x2="1">
          <stop offset="0" stopColor="#e9dccb" />
          <stop offset=".5" stopColor="#fffaf2" />
          <stop offset="1" stopColor="#d9c7b0" />
        </linearGradient>
      </defs>
      <ellipse cx="60" cy="112" rx="42" ry="5" fill="#6E1222" opacity=".15" />
      <rect x="62" y="20" width="16" height="70" rx="5" fill="url(#btl)" />
      <rect x="65" y="12" width="10" height="10" rx="2" fill="#C9A26A" />
      <rect x="80" y="34" width="20" height="62" rx="6" fill="url(#btl)" />
      <rect x="84" y="26" width="12" height="9" rx="2" fill="#C9A26A" />
      <rect x="83" y="56" width="14" height="14" rx="2" fill="#951E30" opacity=".25" />
      <path d="M14 46h58l-4 64H18z" fill="url(#bgw)" />
      <path d="M28 48c0-22 30-22 30 0" fill="none" stroke="#E9C58C" strokeWidth="3" />
      <image href={nlIcon} x="22" y="68" width="42" height="20" filter="url(#bagLogo)" />
      <path d="M14 46h58l-1 8H15z" fill="#000" opacity=".12" />
    </svg>
  );
}

function RecruitIllustration() {
  return (
    <svg viewBox="0 0 120 120">
      <defs>
        <linearGradient id="pw" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#B42A40" />
          <stop offset="1" stopColor="#6E1222" />
        </linearGradient>
        <linearGradient id="gb" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#A8263A" />
          <stop offset="1" stopColor="#5E0F1E" />
        </linearGradient>
        <linearGradient id="gd" x1="0" x2="1">
          <stop offset="0" stopColor="#B9822B" />
          <stop offset=".5" stopColor="#F7E3B8" />
          <stop offset="1" stopColor="#A86E1E" />
        </linearGradient>
      </defs>
      <ellipse cx="60" cy="112" rx="44" ry="5" fill="#6E1222" opacity=".15" />
      <circle cx="44" cy="26" r="13" fill="url(#pw)" />
      <path d="M22 72c0-20 10-32 22-32s22 12 22 32z" fill="url(#pw)" />
      <circle cx="78" cy="30" r="11" fill="url(#pw)" opacity=".85" />
      <path d="M60 74c0-18 8-28 18-28s18 10 18 28z" fill="url(#pw)" opacity=".85" />
      <circle cx="20" cy="62" r="7" fill="url(#pw)" opacity=".7" />
      <path d="M8 92c0-12 5-20 12-20s12 8 12 20z" fill="url(#pw)" opacity=".7" />
      <rect x="36" y="70" width="44" height="38" rx="4" fill="url(#gb)" />
      <rect x="33" y="64" width="50" height="12" rx="3" fill="#B42A40" />
      <rect x="54" y="64" width="8" height="44" fill="url(#gd)" />
      <path d="M58 64c-10-14-22-8-14 0zM58 64c10-14 22-8 14 0z" fill="url(#gd)" />
    </svg>
  );
}

function RetentionIllustration() {
  return (
    <svg viewBox="0 0 120 120">
      <defs>
        <radialGradient id="ht" cx=".35" cy=".3">
          <stop offset="0" stopColor="#D64560" />
          <stop offset=".6" stopColor="#951E30" />
          <stop offset="1" stopColor="#5E0F1E" />
        </radialGradient>
        <linearGradient id="hw" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#B42A40" />
          <stop offset="1" stopColor="#6E1222" />
        </linearGradient>
      </defs>
      <ellipse cx="60" cy="112" rx="44" ry="5" fill="#6E1222" opacity=".15" />
      <path
        d="M74 46C66 40 56 33 56 22c0-7 5-12 11-12 4 0 6 2 7 4 1-2 3-4 7-4 6 0 11 5 11 12 0 11-10 18-18 24z"
        fill="url(#ht)"
      />
      <path d="M66 18c3-3 7-3 9 0" stroke="#fff" strokeOpacity=".5" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M4 70l22-14 14 6-8 30-18 6z" fill="#5E0F1E" />
      <path d="M116 66l-22-12-12 4 8 30 20 8z" fill="#5E0F1E" />
      <path
        d="M28 60c10-6 22-8 32-4l18 8c6 3 4 10-2 9l-14-3c4 3 6 7 2 10-3 2-6 1-8-1-1 4-5 6-9 3-2 3-6 4-9 1l-8-6z"
        fill="url(#hw)"
      />
      <path d="M92 58c-8-4-18-6-28-2l-12 6 10 6 12-3 18 14z" fill="url(#hw)" opacity=".9" />
      <path d="M50 74l6 5M42 77l6 5M58 70l6 5" stroke="#4A0A15" strokeOpacity=".45" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
